import { IMAGES } from '@/assets/images'
import { Container } from '@/types'
import { Box, styled, Typography } from '@mui/material'
import { Folder } from '@mui/icons-material'
import dayjs from 'dayjs'
import React from 'react'
import colors from '@/styles/colors'

interface ContainerGridItemProps {
  item: Container
  onClick: (item: Container) => void
}

const CleanFolderCard = styled(Box)(({ theme }) => ({
  height: 0,
  paddingBottom: '100%',
  borderRadius: '16px',
  cursor: 'pointer',
  position: 'relative',
  backgroundColor: '#ffffff',
  border: '1px solid rgba(226, 232, 240, 0.8)',
  overflow: 'hidden',
  boxShadow: '0 1px 3px rgba(15, 23, 42, 0.08), 0 1px 2px rgba(15, 23, 42, 0.04)',
  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',

  '&::before': {
    content: '""',
    position: 'absolute',
    inset: 0,
    borderRadius: '16px',
    padding: '1px',
    background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.1), rgba(168, 85, 247, 0.1))',
    WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
    WebkitMaskComposite: 'xor',
    maskComposite: 'exclude',
    opacity: 0,
    transition: 'opacity 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
  },

  '&:hover': {
    transform: 'translateY(-6px)',
    boxShadow: '0 12px 40px rgba(99, 102, 241, 0.12), 0 4px 16px rgba(15, 23, 42, 0.08)',
    borderColor: 'rgba(99, 102, 241, 0.2)',

    '&::before': {
      opacity: 1,
    },

    '& .folder-info': {
      background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
    },

    '& .folder-title': {
      color: '#ffffff',
    },

    '& .folder-count': {
      color: 'rgba(255, 255, 255, 0.95)',
    },

    '& .folder-preview::after': {
      opacity: 1,
    },
  },

  '& .folder-preview': {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '70%',
    overflow: 'hidden',
    backgroundColor: '#f8fafc',

    '&::after': {
      content: '""',
      position: 'absolute',
      bottom: 0,
      left: 0,
      right: 0,
      height: '40%',
      background: 'linear-gradient(transparent, rgba(0,0,0,0.15))',
      opacity: 0,
      transition: 'opacity 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
    },
  },

  '& .folder-info': {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    height: '30%',
    backgroundColor: '#ffffff',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    padding: '14px 18px',
    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
    borderTop: '1px solid rgba(226, 232, 240, 0.8)',
  },

  '& .folder-title': {
    fontSize: '15px',
    fontWeight: 700,
    color: '#0f172a',
    lineHeight: 1.2,
    letterSpacing: '-0.01em',
    marginBottom: '4px',
    transition: 'color 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
  },

  '& .folder-count': {
    fontSize: '13px',
    fontWeight: 600,
    color: '#64748b',
    lineHeight: 1,
    letterSpacing: '-0.01em',
    transition: 'color 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
  },
}))

const ContainerGridItem = ({ item, onClick }: ContainerGridItemProps) => {
  const snippetCount = item.photoSnippet?.length || 0
  const imageURL =
    snippetCount > 0
      ? item.photoSnippet[snippetCount - 1].photoAlbumURL
      : IMAGES.topicPreviewPlaceholder

  const photoCount = Number(item.photoCount || 0)
  const monthYear = dayjs(item.datePurchased).format('MMMM YYYY')

  return (
    <CleanFolderCard onClick={() => onClick(item)}>
      {/* Photo Preview */}
      <Box className='folder-preview'>
        {imageURL && photoCount > 0 ? (
          <Box
            sx={{
              width: '100%',
              height: '100%',
              backgroundImage: `url(${imageURL})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
            }}
          />
        ) : (
          <Box
            sx={{
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Folder
              sx={{
                fontSize: '48px',
                color: colors.lightText,
                opacity: 0.3,
              }}
            />
          </Box>
        )}
      </Box>

      {/* Folder Info */}
      <Box className='folder-info'>
        <Typography className='folder-title'>{monthYear}</Typography>
        <Typography className='folder-count'>
          {photoCount} photo{photoCount === 1 ? '' : 's'}
        </Typography>
      </Box>
    </CleanFolderCard>
  )
}

export default ContainerGridItem
